// Catalog of customizable "stat cards" shown above the Student Roster table (demo theme only).
// Each entry knows how to count itself from the roster tuples ([id, student]) the page already has.
// User preferences persist only the ordered list of card KEYS (see studentRoster preferences);
// the labels/colors/compute live here so the stored preference stays small and forward-compatible.
import { elOptions } from './+store/studentRoster/studentRoster.utils';

// The roster reaching the page is UI-shaped (parseStudentDbToUi), so indicator fields are display
// strings ("Non-LEP", "Current English Learner", "Yes", ...). Map EL display labels back to codes.
const EL_DISPLAY_TO_CODE = Object.entries(elOptions).reduce((acc, [code, label]) => {
  acc[label] = code;
  return acc;
}, {});

const lepCode = (student) => {
  const raw = student.lep == null ? '' : String(student.lep).trim();
  if (raw === '') return 'N';
  return EL_DISPLAY_TO_CODE[raw] || raw; // display label -> code, else assume it's already a code
};

const isYes = (v) => v === 'Yes' || v === 'Y' || v === true;
const hasStaarTest = (student) =>
  Array.isArray(student.tests) && student.tests.some((t) => t && t.subject && t.subject.toLowerCase() !== 'writing');

const countWhere = (roster, predicate) => roster.filter(([, student]) => predicate(student)).length;

// Order here is the order cards appear in the "add" tray. DEFAULT_STAT_CARD_KEYS sets the
// out-of-the-box visible set; users can remove/add/reorder from there.
export const STAT_CARD_CATALOG = [
  { key: 'total', label: 'Total students', compute: (roster) => roster.length },
  {
    key: 'withTests',
    label: 'With STAAR tests',
    color: '#16a34a',
    compute: (roster) => countWhere(roster, hasStaarTest),
  },
  { key: 'inSnapshot', label: 'In snapshot', compute: (roster) => countWhere(roster, (s) => isYes(s.snapshot)) },
  {
    key: 'highFocus',
    label: 'High Focus',
    color: '#d97706',
    // Mirrors the accountability definition (functions AFReports.utils highFocusProperties):
    // current-EL OR highly-mobile OR current-SpEd OR eco-dis.
    compute: (roster) =>
      countWhere(
        roster,
        (s) => lepCode(s) === 'C' || isYes(s.highlyMobile) || s.spEd === 'Current' || s.spEd === 'C' || isYes(s.ecoDis)
      ),
  },
  {
    key: 'englishLearners',
    label: 'English learners',
    compute: (roster) => countWhere(roster, (s) => lepCode(s) === 'C'),
  },
  {
    key: 'monitoredEl',
    label: 'EL monitors',
    compute: (roster) => countWhere(roster, (s) => ['F', 'S', '3', '4', '1', '2'].includes(lepCode(s))),
  },
  { key: 'ecoDis', label: 'Economically disadv.', compute: (roster) => countWhere(roster, (s) => isYes(s.ecoDis)) },
  {
    key: 'specialEd',
    label: 'Special education',
    compute: (roster) => countWhere(roster, (s) => s.spEd === 'Current' || s.spEd === 'C'),
  },
  {
    key: 'highlyMobile',
    label: 'Highly mobile',
    compute: (roster) => countWhere(roster, (s) => isYes(s.highlyMobile)),
  },
  {
    key: 'asylee',
    label: 'Asylee / Refugee / SIFE',
    compute: (roster) => countWhere(roster, (s) => isYes(s.asylumStatus)),
  },
  {
    key: 'continuouslyEnrolled',
    label: 'Continuously enrolled',
    compute: (roster) => countWhere(roster, (s) => s.enrollment === 'Continuously Enrolled'),
  },
  {
    key: 'accelMath',
    label: 'Accel. tester: Math',
    compute: (roster) => countWhere(roster, (s) => isYes(s.acceleratedTesterMath)),
  },
  {
    key: 'accelReading',
    label: 'Accel. tester: Reading',
    compute: (roster) => countWhere(roster, (s) => isYes(s.acceleratedTesterReading)),
  },
  {
    key: 'accelScience',
    label: 'Accel. tester: Science',
    compute: (roster) => countWhere(roster, (s) => isYes(s.acceleratedTesterScience)),
  },
];

export const CATALOG_BY_KEY = STAT_CARD_CATALOG.reduce((acc, card) => {
  acc[card.key] = card;
  return acc;
}, {});

export const DEFAULT_STAT_CARD_KEYS = ['total', 'withTests', 'inSnapshot', 'highFocus', 'englishLearners'];

// Keep only known keys, drop dupes, preserve order. A non-array (never-saved) preference falls back
// to the defaults; an explicit empty array is honored (the user removed every card).
export const sanitizeStatCardKeys = (keys) => {
  if (!Array.isArray(keys)) return [...DEFAULT_STAT_CARD_KEYS];
  const seen = new Set();
  const out = [];
  keys.forEach((k) => {
    if (CATALOG_BY_KEY[k] && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  });
  return out;
};
